import { Injectable } from "@angular/core";
import { Chart } from "chart.js";
import { BehaviorSubject, Observable } from "rxjs";
import { LoginViewModel } from "@tylertech/tyler-api-types";
import { SecurityService } from "./security.service";
import { getLoggerFor } from "@tylertech/logging";

export interface ITheme
{
    readonly name: string;
    readonly cssClass: string;
    readonly fontColor: string;
    readonly sidebarColor: string;
    readonly iconCommandColor: string;
    readonly aggregateElementColor: string;
    readonly tooltipBackgroundColor: string;
}

const lightTheme: ITheme = {
    name: "tyler-default-theme",
    cssClass: "tyler-default-theme",
    fontColor: "rgba(0,0,0,0.6)",
    sidebarColor: "#FFFFFF",
    iconCommandColor: "rgba(0,0,0,0.26)",
    aggregateElementColor: "rgba(0,0,0,0.87)",
    tooltipBackgroundColor: "rgba(255, 255, 255, 0.9)",
};
const darkTheme: ITheme = {
    name: "tyler-dark-theme",
    cssClass: "md-tyler-dark-theme-theme",
    fontColor: "rgba(255, 255, 255, 0.6)",
    sidebarColor: "#1e1e1e",
    iconCommandColor: "rgba(255, 255, 255, 0.26)",
    aggregateElementColor: "rgba(255, 255, 255, .87)",
    tooltipBackgroundColor: "rgba(0,0,0,0.9)",
};

/** @deprecated - does this actually do anything? */
@Injectable()
export class ThemeService
{
    private readonly log = getLoggerFor("ThemeService");
    private readonly themeChangeSubject = new BehaviorSubject<ITheme>(lightTheme);
    private readonly themeChangeObservable: Observable<ITheme>;
    constructor(
        $securityService: SecurityService)
    {
        this.themeChangeObservable = this.themeChangeSubject.asObservable();

        $securityService.principleChange
            .subscribe((newPrinciple: LoginViewModel) =>
            {
                if (!newPrinciple)
                {
                    this.setTheme(this.lightTheme);
                    return;
                }

                this.setTheme(newPrinciple.Settings.Theme);
            });

        this.setTheme(lightTheme);
    }

    get currentTheme(): ITheme { return this.themeChangeSubject.value; }
    get lightTheme(): ITheme { return lightTheme; }
    get darkTheme(): ITheme { return darkTheme; }

    get themeChange(): Observable<ITheme> { return this.themeChangeObservable; }

    // TODO library/charting should adding a listener to the themeChange observable
    private setChartTheme(): void
    {
        if (!this.currentTheme)
        {
            this.log.warning("setChartTheme() | No theme is currently set!");
            return;
        }

        Chart.defaults.color = this.currentTheme.fontColor;
        if (!Chart.defaults.plugins)
        {
            return;
        }

        if (Chart.defaults.plugins.legend?.labels)
        {
            Chart.defaults.plugins.legend.labels.color = this.currentTheme.fontColor;
        }

        if (Chart.defaults.plugins.tooltip)
        {
            Chart.defaults.plugins.tooltip.bodyColor = this.currentTheme.fontColor;
            Chart.defaults.plugins.tooltip.footerColor = this.currentTheme.fontColor;
            Chart.defaults.plugins.tooltip.titleColor = this.currentTheme.fontColor;
            Chart.defaults.plugins.tooltip.backgroundColor = this.currentTheme.tooltipBackgroundColor;
        }

        if (Chart.defaults.plugins.title)
        {
            Chart.defaults.plugins.title.color = this.currentTheme.fontColor;
        }
    }

    setTheme(theme: ITheme): void
    {
        this.themeChangeSubject.next(theme);

        this.setChartTheme();
        this.themeElement(document.body);
    }

    private themeElement(element: HTMLElement): void
    {
        if (!this.currentTheme)
        {
            this.log.warning("themeElement() | No theme is currently set!");
            return;
        }

        // TODO - do we need to remove the previous theme class?????
        element.classList.add(this.currentTheme.cssClass);
    }
}
